import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {ClubCurrencyType} from '../../enum/club-currency';
import {CustomerTranslations, CombinedCustomer, CustomerScore} from '../../models';
import {CustomerScoreTranslationsService} from 'src/app/shared/translations';
import {environment} from '~environments/environment';
import {Customer, State, User} from '../../models';
import {
  CustomerAddressTranslationsService,
  CustomerStatusTranslationsService,
  MembershipStatusTranslationsService,
  SalutationsTranslationService,
} from 'src/app/shared/translations';


/**
 * CRUD service for the contact entity.
 */
@Injectable()
export class CustomerService {
  public API_URL: any = `${environment.envAPI}`;
  public API_URL_CUSTOMERS: any = `${environment.envAPI}/customers`;
  public API_URL_CLUBS: any = `${environment.envAPI}/clubs`;
  public API_URL_SCORE: any = `${environment.envAPI}/customer-scores`;
  public API_URL_MERGE: any = `${environment.envAPI}/merge-customers`;
  public currentUser: Observable<User>;
  public lastSearch: {};
  public translations: CustomerTranslations;

  public lastSearchUpdated: BehaviorSubject<any> = new BehaviorSubject(null);
  public castLastSearchUpdated: any = this.lastSearchUpdated.asObservable();

  public customerSearchResults: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  public castCustomerSearchResults: any = this.customerSearchResults.asObservable();

  public selectedCustomer: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public castSelectedCustomer: any = this.selectedCustomer.asObservable();

  public searchOptions: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public castSearchOptions: any = this.searchOptions.asObservable();

  constructor(
    private http: HttpClient,
    private addressTranslations: CustomerAddressTranslationsService,
    private statusTranslations: CustomerStatusTranslationsService,
    private membershipTranslations: MembershipStatusTranslationsService,
    private salutationsTranslations: SalutationsTranslationService,
    private customerScoreTranslations: CustomerScoreTranslationsService,
  ) {
    this.translations = {
      addressTranslations: this.addressTranslations,
      statusTranslations: this.statusTranslations,
      membershipTranslations: this.membershipTranslations,
      salutationsTranslations: this.salutationsTranslations
    };
  }

  /**
   * Get customer by id.
   */
  public getById(id: number): Observable<Customer> {
    return this.http.get<Customer>(`${this.API_URL_CUSTOMERS}/${id}`).pipe(
      map(customer => new Customer(customer, this.translations))
    );
  }

  /**
   * Create a new customer.
   */
  public create(user: Customer): Observable<Customer> {
    return this.http.post<Customer>(this.API_URL_CUSTOMERS, user).pipe(
      map(customer => new Customer(customer, this.translations))
    );
  }

  /**
   * Update a customer.
   */
  public update(customer: Customer): Observable<Customer> {
    return this.http.patch<Customer>(`${this.API_URL_CUSTOMERS}/${customer.id}`, customer).pipe(
      map(data => new Customer(data, this.translations))
    );
  }

  /**
   * Delete a customer.
   */
  public delete(id: number): any {
    return this.http.delete(`${this.API_URL_CUSTOMERS}/${id}`);
  }

  /**
   * Marks the user as primary.
   *
   * Empty body has been added to this HTTP call because of issues with Akamai. Akamai expects that "content-length"
   * HTTP header is always present within this PATCH request, otherwise denial of service attack could be done.
   */
  public markAsPrimary(customerId: number): Observable<Customer> {
    return this.http.patch<Customer>(`${this.API_URL_CUSTOMERS}/${customerId}/mark-primary`, {}).pipe(
      map(customer => new Customer(customer, this.translations))
    );
  }

  /**
   * Get the scores for customer.
   */
  public getCustomerScore(customerId: number, preferredCurrency: ClubCurrencyType): Observable<any> {
    return this.http.get(`${this.API_URL_SCORE}/${customerId}`).pipe(
      map(response => new CustomerScore(response, this.customerScoreTranslations, preferredCurrency))
    );
  }

  /**
   * Search customers by a provided query in advanced elastic search.
   */
  public advancedSearchBy(query: string, clubId: number): Observable<Array<Customer>> {
    const API = `${this.API_URL_CLUBS}/${clubId}/customers/advanced-search`;
    return this.http.get(`${API}?clubId=${clubId}&${query}`).pipe(
      map((response: Array<any>) => response['hydra:member'].map(user => new Customer(user, this.translations)))
    );
  }

  /**
   * Search customers that are potential duplicates.
   */
  public searchDuplicates(customerId: number): Observable<Array<Customer>> {
    return this.http.get(`${this.API_URL_CUSTOMERS}/${customerId}/possible-duplicates`).pipe(
      map((response: Array<any>) => response['hydra:member'].map(user => new Customer(user, this.translations)))
    );
  }

  public removeDuplicates(data): Observable<any> {
    return this.http.post(`${this.API_URL}/remove-possible-duplicate-tracking`, data);
  }

  public searchEmail(query: string, clubId: number): Observable<Customer> {
    return this.http.get(`${this.API_URL_CLUBS}/${clubId}/customers/existing?${query}`).pipe(
      map((response: any) => new Customer(response, this.translations))
    );
  }

  /**
   * Merge the customer.
   */
  public mergeCustomer(customer: CombinedCustomer): Observable<any> {
    return this.http.post<Customer>(this.API_URL_MERGE, customer);
  }

  /**
   * Update the customer state.
   */
  public updateCustomerState(state: State, customerId: number): Observable<Customer> {
    return this.http.patch<State>(`${this.API_URL_CUSTOMERS}/${customerId}/state`, state).pipe(
      map(data => new Customer(data, this.translations))
    );
  }

  /**
   * Update the customer email.
   */
  public updateCustomerEmail(email, customerId: number): Observable<Customer> {
    return this.http.patch<State>(`${this.API_URL_CUSTOMERS}/${customerId}/email`, email).pipe(
      map(data => new Customer(data, this.translations))
    );
  }

  /**
   * Get customer search api.
   */
  public getCustomerSearchEndpoint(isBasic: boolean, clubId: number): any {
    const api = isBasic ? `${this.API_URL_CLUBS}/${clubId}/customers/basic-search` : `${this.API_URL_CLUBS}/${clubId}/customers/advanced-search`;

    const params: Array<any> = [];

    return [api, params];
  }

  public setSearchQuery(query: string, type: string, byEnter: boolean = false): any {
    this.searchOptions.next({query, type, byEnter});
  }

  public createCustomerObject(customers: Array<any>): Array<Customer> {
    return customers.map(customer => new Customer(customer, this.translations));
  }

  /**
   * Set the last results query.
   */
  public setLastSearchResults(query: {}): void {
    this.lastSearch = query;
    this.lastSearchUpdated.next(this.lastSearch);
  }

  /**
   * Change the customer type.
   */
  public toggleCustomerType(id): Observable<any> {
    return this.http.patch<any>(`${this.API_URL_CUSTOMERS}/${id}/convert-type`, {});
  }

  /**
   * Sets the email as shop authorization.
   */
  public setShopAuthorization(customer: Customer): Observable<Customer> {
    return this.http.patch<Customer>(`${this.API_URL_CUSTOMERS}/${customer.id}/authorization`, {}).pipe(
      map(data => new Customer(data, this.translations))
    );
  }

  /**
   * Adds values to the observer.
   */
  public setSelectedCustomer(customer: Customer): void {
    this.selectedCustomer.next(customer);
  }

  /**
   * Adds values to the observer.
   */
  public clearSelecterCustomer(): void {
    this.selectedCustomer.next(null);
  }

  /**
   * Set search results
   */
  public setCustomerSearchResults(customers: any): void {
    this.customerSearchResults.next(customers);
  }

}
